import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ThemeToggle } from "./ui/theme/ThemeToggle";
import { Slack, Home, User, Folder, Mail, Award } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

function cn(...classes) {
return classes.filter(Boolean).join(" ");
}

export default function NavBar() {
const navigate = useNavigate();
const location = useLocation();
const [scrollY, setScrollY] = useState(0);
const [isMobile, setIsMobile] = useState(
typeof window !== "undefined" ? window.innerWidth < 768 : false
);
const [activeSection, setActiveSection] = useState("home");

const items = useMemo(
() => [
{ id: "home", label: "Home", icon: <Home className="h-5 w-5" /> },
{ id: "education", label: "About", icon: <User className="h-5 w-5" /> },
{ id: "projects", label: "Projects", icon: <Folder className="h-5 w-5" /> },
{ id: "certifications", label: "Certificates", icon: <Award className="h-5 w-5" /> },
{ id: "contact", label: "Contact", icon: <Mail className="h-5 w-5" /> },
],
[]
);

const scrollWithOffset = useCallback(
(id) => {
const el = document.getElementById(id);
if (!el) return;
const offset = isMobile ? 0 : 70;
window.scrollTo({ top: el.offsetTop - offset, behavior: "smooth" });
},
[isMobile]
);

const onClickItem = (id) => (e) => {
e.preventDefault();

if (location.pathname !== "/") {
  navigate("/");
  setTimeout(() => {
    scrollWithOffset(id);
  }, 100);
  return;
}

setActiveSection(id);
scrollWithOffset(id);


};

useEffect(() => {
const onScroll = () => setScrollY(window.scrollY);
const onResize = () => setIsMobile(window.innerWidth < 768);
window.addEventListener("scroll", onScroll, { passive: true });
window.addEventListener("resize", onResize, { passive: true });
return () => {
window.removeEventListener("scroll", onScroll);
window.removeEventListener("resize", onResize);
};
}, []);

useEffect(() => {
if (location.pathname !== "/") {
setActiveSection("");
return;
}


const ids = ["home", "education", "projects", "certifications", "contact"];

const handleScroll = () => {
  const scrollPosition = window.scrollY + window.innerHeight / 2;
  const pageBottom =
    window.innerHeight + window.scrollY >= document.body.offsetHeight - 10;

  if (pageBottom) {
    setActiveSection("contact");
    return;
  }

  let current = "home";
  for (const id of ids) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el.offsetTop <= scrollPosition) current = id;
  }
  setActiveSection(current);
};

window.addEventListener("scroll", handleScroll, { passive: true });
handleScroll();
return () => window.removeEventListener("scroll", handleScroll);


}, [location.pathname]);

const maxWidth = isMobile ? 100 : 70;
const minWidth = isMobile ? 100 : 50;
const maxScroll = 200;
const navWidthPercent = Math.max(
minWidth,
maxWidth - ((scrollY / maxScroll) * (maxWidth - minWidth))
);

return (
<>
{/* 🔥 THIS FIXES THE WHITE OUTLINE */} <style>
{`           a:focus,
          a:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
        `} </style>


  <div
    className={cn(
      "fixed left-0 z-50 flex w-full justify-center transition-all duration-300",
      isMobile ? "bottom-0" : "top-2"
    )}
  >
    <div
      className={cn(
        "flex items-center justify-between bg-background/40 backdrop-blur-xl",
        isMobile
          ? "w-full border-t border-white/5 px-2"
          : "rounded-full px-4 py-2 border border-white/5"
      )}
      style={!isMobile ? { width: `${navWidthPercent}%` } : {}}
    >
      {!isMobile && (
        <div className="pl-2">
          <Slack className="h-5 w-5" />
        </div>
      )}

      {isMobile ? (
        <div className="grid w-full grid-cols-5 gap-1 py-2">
          {items.map((it) => (
            <NavLink
              key={it.id}
              href={`#${it.id}`}
              onClick={onClickItem(it.id)}
              active={activeSection === it.id}
              isMobile
              label={it.label}
            >
              {it.icon}
            </NavLink>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-6 text-sm">
          {items.map((it) => (
            <NavLink
              key={it.id}
              href={`#${it.id}`}
              onClick={onClickItem(it.id)}
              active={activeSection === it.id}
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}

      {!isMobile && (
        <div className="pr-1">
          <ThemeToggle />
        </div>
      )}
    </div>
  </div>
</>


);
}

function NavLink({ href, children, active, isMobile, label, onClick }) {
const [hovered, setHovered] = useState(false);

if (isMobile) {
return (
<a
href={href}
onClick={onClick}
className={cn(
"flex flex-col items-center text-xs",
active ? "text-primary" : "text-muted-foreground"
)}
>
{children} <span>{label}</span> </a>
);
}

return (
<a
href={href}
onClick={onClick}
onMouseEnter={() => setHovered(true)}
onMouseLeave={() => setHovered(false)}
style={{
display: "flex",
alignItems: "center",
gap: "8px",
cursor: "pointer",
textDecoration: "none",
}}
>
<span
style={{
height: "8px",
width: "8px",
borderRadius: "9999px",
backgroundColor: "#22c55e",
transition: "opacity 0.2s",
opacity: active ? 1 : 0,
flexShrink: 0,
}}
/>


  <span
    style={{
      display: "inline-block",
      overflow: "hidden",
      height: "1.25em",
      lineHeight: "1.25em",
      position: "relative",
    }}
  >
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        transform: hovered ? "translateY(-50%)" : "translateY(0%)",
        transition: "transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <span
        style={{
          color: active ? "#ffffff" : "#a1a1aa",
          fontWeight: active ? "500" : "400",
        }}
      >
        {children}
      </span>

      <span
        aria-hidden
        style={{
          color: active ? "#ffffff" : "#d4d4d8",
          fontWeight: active ? "500" : "400",
        }}
      >
        {children}
      </span>
    </span>
  </span>
</a>


);
}
